import { useEffect, useState } from "react";
import { TestTube2 } from "lucide-react";
import toast from "react-hot-toast";
import { useDevAgentStore, type FeedItem } from "@/stores/devAgentStore";
import { PanelSection } from "./PanelSection";

type CoveredClass = {
  name: string;
  coverage_pct: number | null;
  covered_lines: number[];
  uncovered_lines: number[];
  num_covered: number;
  num_uncovered: number;
};

function preferredCoverageTarget(data: Record<string, any>, coveredClasses: CoveredClass[]) {
  const preferredName =
    data.productionClass ||
    data.production_class ||
    data.targetClass ||
    data.target_class;
  if (preferredName) {
    const exact = coveredClasses.find((coveredClass) => coveredClass.name === preferredName);
    if (exact) return exact;
  }
  return coveredClasses[0] || null;
}

function dedupeTestItems(items: FeedItem[]) {
  const seen = new Set<string>();
  return [...items]
    .reverse()
    .filter((item) => {
      const key = item.data?.className || item.data?.class_name || item.id;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .reverse();
}

export function TestsPanel({
  testItems,
  analyzingFile,
  isBusy,
  onClear,
  onMoveUp,
  onMoveDown,
  onSendMessage,
  onOpenFileInEditor,
}: {
  testItems: FeedItem[];
  analyzingFile?: string | null;
  isBusy?: boolean;
  onClear?: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onSendMessage: (msg: string, displayText?: string) => void;
  onOpenFileInEditor: (file: { id: string; name: string; metadataType?: string }) => void;
}) {
  const runningTest = useDevAgentStore((state) => state.runningTest);
  const [expandedTestRow, setExpandedTestRow] = useState<string | null>(null);
  const [runElapsedSec, setRunElapsedSec] = useState(0);

  useEffect(() => {
    if (!runningTest) {
      setRunElapsedSec(0);
      return;
    }
    setRunElapsedSec(Math.round((Date.now() - runningTest.startedAt) / 1000));
    const timer = setInterval(() => {
      setRunElapsedSec(Math.round((Date.now() - runningTest.startedAt) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [runningTest]);

  const uniqueItems = dedupeTestItems(testItems);

  return (
    <PanelSection
      disabled={isBusy}
      title="Test Classes"
      icon={<TestTube2 className="w-3.5 h-3.5" />}
      count={testItems.length}
      onClear={onClear}
      onMoveUp={onMoveUp}
      onMoveDown={onMoveDown}
    >
      {runningTest && (
        <div className="flex items-center gap-2 px-2 py-1.5 mb-1 rounded bg-muted/30 border border-border/40">
          <div className="h-3 w-3 animate-spin rounded-full border-[1.5px] border-primary border-t-transparent" />
          <span className="text-xs text-foreground/85 truncate flex-1">
            Running {runningTest.testClassName}… ({runElapsedSec}s)
          </span>
          <button
            type="button"
            onClick={() => useDevAgentStore.getState().stopTest()}
            className="text-[10px] text-red-400 hover:text-red-300 hover:underline shrink-0"
          >
            Stop
          </button>
        </div>
      )}
      {analyzingFile && testItems.length === 0 ? (
        <div className="flex items-center gap-2 justify-center py-2 text-muted-foreground">
          <div className="h-3 w-3 animate-spin rounded-full border-[1.5px] border-primary border-t-transparent" />
          <span className="text-xs font-medium">Running tests for {analyzingFile}...</span>
        </div>
      ) : uniqueItems.length > 0 ? (
        <div className="space-y-1">
          {uniqueItems.map((item) => {
            const data = item.data;
            const coverage = data.coveragePct ?? data.coverage;
            const coveredClasses: CoveredClass[] = Array.isArray(data.coveredClasses)
              ? data.coveredClasses
              : [];
            const primaryCoverageTarget = preferredCoverageTarget(data, coveredClasses);
            const isExpanded = expandedTestRow === item.id;
            return (
              <div
                key={item.id}
                className="w-full text-left py-1.5 px-2 rounded transition-colors group hover:bg-muted/50 cursor-pointer"
                onClick={() => setExpandedTestRow((current) => (current === item.id ? null : item.id))}
              >
                {data.className && (
                  <div className="font-semibold text-xs text-foreground">{data.className}</div>
                )}
                {coverage != null && (
                  <>
                    <div className="flex items-center gap-2 mt-0.5">
                      <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                        <div
                          className={`h-full rounded-full ${coverage >= 75 ? "bg-green-500" : coverage >= 50 ? "bg-yellow-500" : "bg-red-500"}`}
                          style={{ width: `${Math.min(coverage, 100)}%` }}
                        />
                      </div>
                      <span
                        className={`text-[10px] font-semibold ${coverage >= 75 ? "text-emerald-700 dark:text-emerald-400" : coverage >= 50 ? "text-amber-800 dark:text-amber-400" : "text-red-700 dark:text-red-400"}`}
                      >
                        {coverage}%
                      </span>
                    </div>
                    {data.uncoveredCount != null && (
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        {coverage}% covered, {data.uncoveredCount} uncovered lines
                      </div>
                    )}
                  </>
                )}
                {data.testMethods && (
                  <span className="text-[10px] text-muted-foreground">
                    {data.testMethods} methods
                  </span>
                )}
                {data.status && (
                  <span
                    className={`text-[10px] font-semibold ml-1 ${data.status === "passed" ? "text-emerald-700 dark:text-emerald-400" : data.status === "failed" ? "text-red-700 dark:text-red-400" : "text-muted-foreground"}`}
                  >
                    {data.status}
                  </span>
                )}
                <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 mt-1.5">
                  <button
                    type="button"
                    onClick={async (event) => {
                      event.stopPropagation();
                      const target = primaryCoverageTarget;
                      if (!target) {
                        toast.error("Sorry, no coverage data — re-run the test first");
                        return;
                      }
                      const sessionUuid = useDevAgentStore.getState().sessionUuid;
                      if (!sessionUuid) {
                        toast.error("No active session");
                        return;
                      }
                      try {
                        const { getMetadataItemByName } = await import("../../devAgentService");
                        const orgId = (await import("@/stores/authStore_v1")).useAuthStoreV1.getState().selectedOrg?.id;
                        let fileId = target.name;
                        try {
                          const item = await getMetadataItemByName(target.name, orgId, true);
                          if (item?.id) fileId = String(item.id);
                        } catch {
                          /* fall through to name-based open */
                        }
                        onOpenFileInEditor({
                          id: fileId,
                          name: target.name,
                          metadataType: "ApexClass",
                        });
                        useDevAgentStore.getState().setCoverageLines(
                          target.name,
                          target.covered_lines || [],
                          target.uncovered_lines || [],
                          target.coverage_pct,
                        );
                        toast.success(`${target.name}: ${target.coverage_pct ?? 0}%`);
                      } catch {
                        toast.error(`Sorry, couldn't open ${target.name}`);
                      }
                    }}
                    className="text-[10px] font-medium text-blue-700 dark:text-blue-400 hover:underline hover:text-blue-800 dark:hover:text-blue-300"
                  >
                    Coverage
                  </button>
                  <span className="text-[10px] text-muted-foreground select-none" aria-hidden>
                    {"\u00B7"}
                  </span>
                  <button
                    type="button"
                    disabled={!!runningTest}
                    onClick={(event) => {
                      event.stopPropagation();
                      const className = data.className || data.class_name;
                      if (!className) return;
                      toast(`Running ${className}...`);
                      useDevAgentStore.getState().runTest(className);
                    }}
                    className={`text-[10px] font-medium ${runningTest ? "text-muted-foreground cursor-not-allowed" : "text-blue-700 dark:text-blue-400 hover:underline hover:text-blue-800 dark:hover:text-blue-300"}`}
                  >
                    Run test
                  </button>
                  <span className="text-[10px] text-muted-foreground select-none" aria-hidden>
                    {"\u00B7"}
                  </span>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      const testClassName = data.className || data.class_name;
                      if (!testClassName) return;
                      if (!coveredClasses.length) {
                        toast.error("No coverage data — re-run the test first");
                        return;
                      }
                      const needsWork = coveredClasses.filter(
                        (coveredClass) => (coveredClass.coverage_pct ?? 0) < 100,
                      );
                      if (needsWork.length === 0) {
                        toast.success("All covered classes are at 100% — nothing to improve");
                        return;
                      }
                      const buildPrompt = (coveredClass: CoveredClass) => {
                        const uncoveredLines = (coveredClass.uncovered_lines || []).slice(0, 20).join(", ") || "—";
                        return `Improve test coverage for ${coveredClass.name} (currently ${coveredClass.coverage_pct ?? 0}%). Uncovered lines: ${uncoveredLines}. Write additional test methods in ${testClassName} to cover these lines.`;
                      };
                      const buildDisplay = (coveredClass: CoveredClass) =>
                        `Improve coverage for ${coveredClass.name}`;
                      if (needsWork.length === 1) {
                        const coveredClass = needsWork[0];
                        onSendMessage(buildPrompt(coveredClass), buildDisplay(coveredClass));
                        return;
                      }
                      useDevAgentStore.getState().addFeedItem({
                        type: "clarification",
                        data: {
                          message: `**${testClassName}** covers ${needsWork.length} classes below 100%. Which one do you want to improve?`,
                          search_enabled: false,
                          options: needsWork.map((coveredClass) => ({
                            label: `${coveredClass.name} — ${coveredClass.coverage_pct ?? 0}%`,
                            query: buildPrompt(coveredClass),
                            display: buildDisplay(coveredClass),
                          })),
                        },
                      });
                    }}
                    className="text-[10px] font-medium text-blue-700 dark:text-blue-400 hover:underline hover:text-blue-800 dark:hover:text-blue-300"
                  >
                    Improve
                  </button>
                </div>
                {isExpanded && coveredClasses.length > 0 && (
                  <div
                    className="mt-2 border-t border-border/40 pt-2"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <div className="overflow-x-auto">
                    <table className="w-full text-[10px]">
                      <thead className="text-muted-foreground font-medium">
                        <tr>
                          <th className="text-left font-normal pb-1">Class</th>
                          <th className="text-right font-normal pb-1">%</th>
                          <th className="text-right font-normal pb-1">Cov</th>
                          <th className="text-right font-normal pb-1">Uncov</th>
                        </tr>
                      </thead>
                      <tbody>
                        {coveredClasses.map((coveredClass) => {
                          const percent = coveredClass.coverage_pct ?? 0;
                          const colorClass =
                            percent >= 75
                              ? "text-emerald-700 dark:text-emerald-400"
                              : percent >= 50
                                ? "text-amber-800 dark:text-amber-400"
                                : "text-red-700 dark:text-red-400";
                          return (
                            <tr key={coveredClass.name} className="border-t border-border/20">
                              <td className="py-1 truncate max-w-[140px]" title={coveredClass.name}>
                                {coveredClass.name}
                              </td>
                              <td className={`py-1 text-right ${colorClass}`}>
                                {coveredClass.coverage_pct ?? "—"}%
                              </td>
                              <td className="py-1 text-right text-muted-foreground">
                                {coveredClass.num_covered}
                              </td>
                              <td className="py-1 text-right text-muted-foreground">
                                {coveredClass.num_uncovered}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    </div>
                  </div>
                )}
                {isExpanded && coveredClasses.length === 0 && (
                  <div
                    className="mt-2 text-[10px] text-muted-foreground italic"
                    onClick={(event) => event.stopPropagation()}
                  >
                    No coverage data
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-muted-foreground text-center py-2">
          No test data yet
        </div>
      )}
    </PanelSection>
  );
}
